// Renders Toast POS menu data into the host page. Opt-in via the
// `renderToast` option: when enabled, the authored `[data-menu="menu-title"]`
// element is treated as a template and cloned once per menu, with its text
// swapped for that menu's name.

/** Selector for the authored wrapper that acts as the per-menu template. It
 * holds the menu-title and menu-group templates and is cloned once per menu. */
const MENU_SELECTOR = '[data-menu="menu"]';

/** Selector for the authored heading that acts as the menu-title template. */
const MENU_TITLE_SELECTOR = '[data-menu="menu-title"]';

/** Selector for the authored wrapper that acts as the menu-group template,
 * cloned once per menu group inside its parent menu wrapper. */
const MENU_GROUP_SELECTOR = '[data-menu="menu-group"]';

/** Selector for the heading inside a menu-group wrapper that receives the
 * group's name. */
const MENU_GROUP_TITLE_SELECTOR = '[data-menu="menu-group-title"]';

/** Selector for the authored wrapper that acts as the menu-item template,
 * cloned once per menu item inside its parent menu-group wrapper. */
const MENU_ITEM_SELECTOR = '[data-menu="item"]';

/** Selector for the element inside a menu-item wrapper that receives the
 * item's name. */
const MENU_ITEM_TITLE_SELECTOR = '[data-menu="item-title"]';

/** Selector for the element inside a menu-item wrapper that receives the
 * item's description. */
const MENU_ITEM_DESCRIPTION_SELECTOR = '[data-menu="item-description"]';

/** Selector for the element inside a menu-item wrapper that receives the
 * item's price. */
const MENU_ITEM_PRICE_SELECTOR = '[data-menu="item-price"]';

/** Selector for the authored top-nav item that acts as a template, cloned once
 * per menu into a link that jumps to that menu's section. */
const TOP_NAV_ITEM_SELECTOR = '[data-menu="top-nav-item"]';

/** Selector for the text element inside a top-nav item that receives the menu
 * name. */
const TOP_NAV_ITEM_TEXT_SELECTOR = '[data-menu="top-nav-item-text"]';

/** Attribute stamped onto a generated top-nav item recording the id of the
 * menu section it points at, so the section detector can find the nav item to
 * highlight from the section that scrolled into view. */
const NAV_TARGET_ATTR = "data-tv-nav-target";

/** Class toggled on a top-nav item while its menu section is in the viewport. */
const ACTIVE_TOP_NAV_CLASS = "active-top-nav";

/** Selector for the authored sub-nav wrapper, cloned once per top-level menu
 * into a list of that menu's group names. Only the clone for the menu currently
 * in the viewport is shown. */
const SUB_NAV_ITEMS_SELECTOR = '[data-menu="sub-nav-items"]';

/** Selector for the authored sub-nav entry inside a sub-nav wrapper, cloned
 * once per menu group. */
const SUB_NAV_ITEM_SELECTOR = '[data-menu="sub-nav-item"]';

/** Selector for the text element inside a sub-nav entry that receives the menu
 * group name. */
const SUB_NAV_ITEM_TEXT_SELECTOR = '[data-menu="sub-nav-item-text"]';

/** Attribute stamped onto a generated sub-nav item recording the id of the
 * menu-group section it points at, so the group detector can find the sub-nav
 * item to highlight from the group that scrolled into view. */
const SUB_NAV_TARGET_ATTR = "data-tv-subnav-target";

/** Class toggled on a sub-nav item while its menu-group section is in the
 * viewport. */
const ACTIVE_SUB_NAV_CLASS = "active-sub-nav";

/** Height of the sticky nav, in rem. Used to discount the top of the viewport
 * hidden behind the nav from the section detectors, and to land anchor scrolls
 * just below the nav rather than under it. */
const NAV_OFFSET_REM = 6.5;

/** Converts rem to pixels against the document root font size (falling back to
 * the 16px browser default when it can't be read). */
function remToPx(rem: number): number {
  const root = parseFloat(getComputedStyle(document.documentElement).fontSize);
  return rem * (Number.isFinite(root) ? root : 16);
}

/** `IntersectionObserver` options that shrink the viewport's top edge by the
 * sticky nav's height, so a section hidden behind the nav isn't counted as in
 * view. Recomputed per render so it tracks the current root font size. */
function sectionObserverOptions(): IntersectionObserverInit {
  return { rootMargin: `-${remToPx(NAV_OFFSET_REM)}px 0px 0px 0px` };
}

/** Prefix for the ids stamped onto generated menu sections so the matching
 * top-nav item can scroll to them. */
const MENU_SECTION_ID_PREFIX = "tv-menu-";

/** The unique id for a generated menu section, keyed by its position in the
 * menus array. */
function menuSectionId(menuIndex: number): string {
  return `${MENU_SECTION_ID_PREFIX}${menuIndex}`;
}

/** The unique id for a generated menu group, namespaced under its parent menu's
 * id so it stays unique across the whole page. */
function menuGroupSectionId(menuIndex: number, groupIndex: number): string {
  return `${menuSectionId(menuIndex)}-group-${groupIndex}`;
}

/** Selector for the authored wrapper whose contents stay hidden until the
 * Toast menus have loaded and rendered. */
const CONTAINER_SELECTOR = '[data-menu="container"]';

/** Selector for an optional author-supplied loader. When present it's shown
 * (and hidden again on load) instead of the injected spinner; author it as a
 * sibling of the container so hiding the container doesn't hide it too. */
const LOADER_SELECTOR = '[data-menu="loader"]';

/** Marks nodes this script generates so re-renders replace them rather than
 * stack more copies on top (a background revalidate can call render again). */
const GENERATED_ATTR = "data-tv-generated";

/** Marks the spinner this script injects while the venue data is in flight. */
const LOADING_ATTR = "data-tv-loading";

/** id of the one-time <style> block backing the injected spinner. */
const LOADER_STYLE_ID = "tv-loader-styles";

/** A single menu item within a menu group; only the name and description are
 * used here. */
interface ToastMenuItem {
  name?: unknown;
  description?: unknown;
  price?: unknown;
  [key: string]: unknown;
}

/** A single menu group within a Toast menu; only the display name and its
 * items are used. */
interface ToastMenuGroup {
  name?: unknown;
  menuItems?: unknown;
  [key: string]: unknown;
}

/** A single Toast menu; only the display name and its groups are used here. */
interface ToastMenu {
  name?: unknown;
  menuGroups?: unknown;
  [key: string]: unknown;
}

/**
 * Pulls the Toast menus array out of a venue payload, following
 * `toastMenu.json.menus`. Returns an empty array if any hop is missing or the
 * value isn't an array, so callers never have to null-check.
 */
function extractMenus(venue: unknown): ToastMenu[] {
  const menus = (venue as any)?.toastMenu?.json?.menus;
  return Array.isArray(menus) ? menus : [];
}

/**
 * Pulls a menu's `menuGroups` array. Returns an empty array when it's missing
 * or not an array, so callers never have to null-check.
 */
function extractMenuGroups(menu: ToastMenu): ToastMenuGroup[] {
  return Array.isArray(menu.menuGroups) ? menu.menuGroups : [];
}

/**
 * Pulls a group's `menuItems` array. Returns an empty array when it's missing
 * or not an array, so callers never have to null-check.
 */
function extractMenuItems(group: ToastMenuGroup): ToastMenuItem[] {
  return Array.isArray(group.menuItems) ? group.menuItems : [];
}

/**
 * Hides the `[data-menu="container"]` wrapper and shows a loader in its place,
 * so nothing half-populated shows while the venue data is in flight. Uses an
 * author-supplied `[data-menu="loader"]` when one is on the page; otherwise
 * injects a spinner. Idempotent, and cleared by {@link renderToastMenus} once
 * the titles are rendered. No-ops when there's no DOM or no container.
 */
export function showToastLoading(): void {
  if (typeof document === "undefined") return;

  const container = document.querySelector<HTMLElement>(CONTAINER_SELECTOR);
  if (!container) return;

  container.style.display = "none";

  // Prefer the author's own loader; just make sure it's visible.
  const authored = document.querySelector<HTMLElement>(LOADER_SELECTOR);
  if (authored) {
    authored.style.display = "";
    return;
  }

  // No authored loader — inject our spinner (once).
  if (document.querySelector(`[${LOADING_ATTR}]`)) return;
  container.before(createSpinner());
}

/** Builds the injected loader: a centered spinner that inherits the surrounding
 * text color, backed by a one-time <style> block for its keyframes. */
function createSpinner(): HTMLElement {
  if (!document.getElementById(LOADER_STYLE_ID)) {
    const style = document.createElement("style");
    style.id = LOADER_STYLE_ID;
    style.textContent =
      `[${LOADING_ATTR}]{display:flex;justify-content:center;align-items:center;padding:2rem;}` +
      `[${LOADING_ATTR}]::after{content:"";width:2rem;height:2rem;border:3px solid currentColor;` +
      `border-top-color:transparent;border-radius:50%;opacity:.5;animation:tv-spin .8s linear infinite;}` +
      `@keyframes tv-spin{to{transform:rotate(360deg);}}`;
    document.head.appendChild(style);
  }

  const loader = document.createElement("div");
  loader.setAttribute(LOADING_ATTR, "");
  loader.setAttribute("role", "status");
  loader.setAttribute("aria-label", "Loading menu");
  return loader;
}

/** Reveals the container again and hides whichever loader was shown: the
 * author's `[data-menu="loader"]`, or our injected spinner. */
function clearToastLoading(container: HTMLElement | null): void {
  const authored = document.querySelector<HTMLElement>(LOADER_SELECTOR);
  if (authored) authored.style.display = "none";

  document.querySelectorAll(`[${LOADING_ATTR}]`).forEach((el) => el.remove());
  if (container) container.style.display = "";
}

/** Sets an element's text to `name`, blank when it isn't a string. */
function setName(el: HTMLElement, name: unknown): void {
  el.textContent = typeof name === "string" ? name : "";
}

/** Sets an element's text to `price` formatted to two decimals, blank when it
 * isn't a finite number. Left without a currency symbol so authors can add one
 * around the `[data-menu="item-price"]` element in their template. */
function setPrice(el: HTMLElement, price: unknown): void {
  el.textContent =
    typeof price === "number" && Number.isFinite(price)
      ? price.toFixed(2)
      : "";
}

/**
 * Builds one rendered menu from the authored `[data-menu="menu"]` wrapper:
 * clones the wrapper, fills in its `[data-menu="menu-title"]` with the menu
 * name, and replaces the menu-group template with one clone per menu group. The
 * clone is marked generated (and un-hidden) so it survives the cleanup pass
 * that hides the authored template, and stamped with an id so the matching
 * top-nav item can scroll to it.
 */
function buildMenu(
  template: HTMLElement,
  menu: ToastMenu,
  index: number
): HTMLElement {
  const clone = template.cloneNode(true) as HTMLElement;
  clone.setAttribute(GENERATED_ATTR, "");
  clone.style.display = "";
  clone.id = menuSectionId(index);
  // Land top-nav anchor scrolls just below the sticky nav, not under it.
  clone.style.scrollMarginTop = `${NAV_OFFSET_REM}rem`;

  const title = clone.querySelector<HTMLElement>(MENU_TITLE_SELECTOR);
  if (title) setName(title, menu.name);

  // Expand the `[data-menu="menu-group"]` wrapper into one copy per menu group,
  // naming the `[data-menu="menu-group-title"]` inside each and stamping each
  // with a unique id. When the menu has no groups the wrapper is simply
  // removed, leaving no stray placeholder.
  const groupTemplate = clone.querySelector<HTMLElement>(MENU_GROUP_SELECTOR);
  if (groupTemplate) {
    const groups = document.createDocumentFragment();
    extractMenuGroups(menu).forEach((group, groupIndex) => {
      groups.appendChild(
        buildMenuGroup(groupTemplate, group, menuGroupSectionId(index, groupIndex))
      );
    });
    groupTemplate.replaceWith(groups);
  }

  return clone;
}

/**
 * Builds one top-nav item from the authored `[data-menu="top-nav-item"]`
 * template: clones it, fills its `[data-menu="top-nav-item-text"]` with the
 * menu name, and wires a click that smoothly scrolls the matching generated
 * menu section (see {@link MENU_SECTION_ID_PREFIX}) into view. Marked generated
 * (and un-hidden) so it survives the cleanup pass that hides the authored
 * template, matching the section index stamped by {@link buildMenu}, and
 * stamped with {@link NAV_TARGET_ATTR} so {@link observeActiveTopNav} can find
 * it from its section when that section scrolls into view.
 */
function buildTopNavItem(
  template: HTMLElement,
  menu: ToastMenu,
  index: number
): HTMLElement {
  const clone = template.cloneNode(true) as HTMLElement;
  clone.setAttribute(GENERATED_ATTR, "");
  clone.style.display = "";

  const text = clone.querySelector<HTMLElement>(TOP_NAV_ITEM_TEXT_SELECTOR);
  if (text) setName(text, menu.name);

  const targetId = menuSectionId(index);
  clone.setAttribute(NAV_TARGET_ATTR, targetId);
  clone.addEventListener("click", () => {
    document
      .getElementById(targetId)
      ?.scrollIntoView({ behavior: "smooth" });
  });

  return clone;
}

/**
 * Builds one sub-nav from the authored `[data-menu="sub-nav-items"]` wrapper:
 * clones it, and replaces the `[data-menu="sub-nav-item"]` template with one
 * clone per menu group, filling each `[data-menu="sub-nav-item-text"]` with the
 * group name and wiring a click that smoothly scrolls the matching group
 * section (its id stamped by {@link buildMenuGroup} from the same indices) into
 * view. Marked generated (so it survives the cleanup pass) and stamped with
 * {@link NAV_TARGET_ATTR} pointing at the matching menu section, so
 * {@link observeActiveTopNav} can reveal the sub-nav whose menu is in view.
 *
 * Only the first sub-nav is shown by default; the rest start hidden and are
 * revealed by the section detector as their menu scrolls into view.
 */
function buildSubNav(
  template: HTMLElement,
  menu: ToastMenu,
  index: number
): HTMLElement {
  const clone = template.cloneNode(true) as HTMLElement;
  clone.setAttribute(GENERATED_ATTR, "");
  clone.setAttribute(NAV_TARGET_ATTR, menuSectionId(index));
  clone.style.display = index === 0 ? "" : "none";

  const itemTemplate = clone.querySelector<HTMLElement>(SUB_NAV_ITEM_SELECTOR);
  if (itemTemplate) {
    const items = document.createDocumentFragment();
    extractMenuGroups(menu).forEach((group, groupIndex) => {
      const itemClone = itemTemplate.cloneNode(true) as HTMLElement;
      const text = itemClone.querySelector<HTMLElement>(
        SUB_NAV_ITEM_TEXT_SELECTOR
      );
      if (text) setName(text, group.name);

      // Anchor to the matching group section, whose id is stamped by
      // buildMenuGroup with the same menu/group indices.
      const targetId = menuGroupSectionId(index, groupIndex);
      itemClone.setAttribute(SUB_NAV_TARGET_ATTR, targetId);
      itemClone.addEventListener("click", () => {
        document
          .getElementById(targetId)
          ?.scrollIntoView({ behavior: "smooth" });
      });

      items.appendChild(itemClone);
    });
    itemTemplate.replaceWith(items);
  }

  return clone;
}

/**
 * Builds one rendered menu group from the authored `[data-menu="menu-group"]`
 * wrapper: clones it, stamps it with the unique `id`, names its
 * `[data-menu="menu-group-title"]`, and replaces the `[data-menu="item"]`
 * template with one clone per menu item, filling in each item's title,
 * description, and price. When the group has no items the item wrapper is
 * simply removed, leaving no stray placeholder.
 */
function buildMenuGroup(
  template: HTMLElement,
  group: ToastMenuGroup,
  id: string
): HTMLElement {
  const clone = template.cloneNode(true) as HTMLElement;
  clone.id = id;
  // Land sub-nav anchor scrolls just below the sticky nav, not under it.
  clone.style.scrollMarginTop = `${NAV_OFFSET_REM}rem`;

  const nameEl = clone.querySelector<HTMLElement>(MENU_GROUP_TITLE_SELECTOR);
  if (nameEl) setName(nameEl, group.name);

  const itemTemplate = clone.querySelector<HTMLElement>(MENU_ITEM_SELECTOR);
  if (itemTemplate) {
    const items = document.createDocumentFragment();
    for (const item of extractMenuItems(group)) {
      const itemClone = itemTemplate.cloneNode(true) as HTMLElement;
      const itemTitle = itemClone.querySelector<HTMLElement>(
        MENU_ITEM_TITLE_SELECTOR
      );
      if (itemTitle) setName(itemTitle, item.name);
      const itemDescription = itemClone.querySelector<HTMLElement>(
        MENU_ITEM_DESCRIPTION_SELECTOR
      );
      if (itemDescription) setName(itemDescription, item.description);
      const itemPrice = itemClone.querySelector<HTMLElement>(
        MENU_ITEM_PRICE_SELECTOR
      );
      if (itemPrice) setPrice(itemPrice, item.price);
      items.appendChild(itemClone);
    }
    itemTemplate.replaceWith(items);
  }

  return clone;
}

/**
 * Clones the `[data-menu="menu"]` wrapper once per Toast menu, filling each
 * clone's `[data-menu="menu-title"]` with the menu name and expanding its
 * `[data-menu="menu-group"]` template into one element per menu group. The
 * `[data-menu="top-nav-item"]` template is likewise cloned once per menu into a
 * top-nav link that scrolls to the matching section. The original authored
 * wrappers are kept as templates (hidden) so the render is idempotent: calling
 * it again clears the previously generated nodes and rebuilds them from current
 * data.
 *
 * Once the data has arrived this also clears the loading placeholder and
 * reveals the `[data-menu="container"]` wrapper — even when there are no menus
 * or no template to render, so the wrapper is never left hidden for good.
 *
 * No-ops safely when there's no DOM.
 */
export function renderToastMenus(venue: unknown): void {
  if (typeof document === "undefined") return;

  const container = document.querySelector<HTMLElement>(CONTAINER_SELECTOR);
  const menus = extractMenus(venue);

  // The authored templates are the elements that aren't our own clones.
  const menuTemplate = document.querySelector<HTMLElement>(
    `${MENU_SELECTOR}:not([${GENERATED_ATTR}])`
  );
  const navTemplate = document.querySelector<HTMLElement>(
    `${TOP_NAV_ITEM_SELECTOR}:not([${GENERATED_ATTR}])`
  );
  const subNavTemplate = document.querySelector<HTMLElement>(
    `${SUB_NAV_ITEMS_SELECTOR}:not([${GENERATED_ATTR}])`
  );

  if (menus.length > 0) {
    // Drop clones from any previous render — menu sections and top-nav items
    // alike — before rebuilding both from current data.
    document
      .querySelectorAll(`[${GENERATED_ATTR}]`)
      .forEach((el) => el.remove());

    // Expand each authored template into one copy per menu, keeping menu
    // sections and their top-nav items index-aligned so the anchors match.
    if (menuTemplate) expandPerMenu(menuTemplate, menus, buildMenu);
    if (navTemplate) expandPerMenu(navTemplate, menus, buildTopNavItem);
    if (subNavTemplate) expandPerMenu(subNavTemplate, menus, buildSubNav);

    // Watch the freshly generated sections so the matching top-nav item is
    // highlighted, and the matching sub-nav shown, while its menu is in view;
    // and the matching sub-nav item highlighted while its group is in view.
    observeActiveTopNav();
    observeActiveSubNav();
  }

  clearToastLoading(container);
}

/**
 * Clones `template` once per menu via `build`, inserts the copies right after
 * the template, and hides the template itself so only the data-driven copies
 * are visible. The `index` handed to `build` keeps parallel expansions (menu
 * sections and their top-nav anchors) aligned.
 */
function expandPerMenu(
  template: HTMLElement,
  menus: ToastMenu[],
  build: (template: HTMLElement, menu: ToastMenu, index: number) => HTMLElement
): void {
  const fragment = document.createDocumentFragment();
  menus.forEach((menu, index) => {
    fragment.appendChild(build(template, menu, index));
  });
  template.after(fragment);
  template.style.display = "none";
}

/** The observer watching the generated menu sections, kept at module scope so
 * each render can disconnect the previous one before wiring up the new nodes. */
let sectionObserver: IntersectionObserver | null = null;

/** The ids of the generated menu sections currently intersecting the viewport,
 * kept between observer callbacks so {@link showActiveSubNav} can pick the
 * topmost one to reveal. */
const intersectingSections = new Set<string>();

/**
 * Tracks which menu sections are in the viewport and reflects that into the
 * nav: observes every generated `[data-menu="menu"]` section and, as each one
 * enters or leaves view, toggles {@link ACTIVE_TOP_NAV_CLASS} on its top-nav
 * item (matched via {@link NAV_TARGET_ATTR}) and reveals the matching sub-nav
 * through {@link showActiveSubNav}.
 *
 * Disconnects any observer from a previous render and clears its tracked state
 * so stale sections aren't watched, and no-ops where `IntersectionObserver`
 * isn't available.
 */
function observeActiveTopNav(): void {
  sectionObserver?.disconnect();
  intersectingSections.clear();
  if (typeof IntersectionObserver === "undefined") return;

  sectionObserver = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      const sectionId = entry.target.id;

      // Highlight the top-nav item for as long as its menu is in view.
      document
        .querySelector<HTMLElement>(
          `${TOP_NAV_ITEM_SELECTOR}[${NAV_TARGET_ATTR}="${sectionId}"]`
        )
        ?.classList.toggle(ACTIVE_TOP_NAV_CLASS, entry.isIntersecting);

      if (entry.isIntersecting) intersectingSections.add(sectionId);
      else intersectingSections.delete(sectionId);
    }
    showActiveSubNav();
  }, sectionObserverOptions());

  document
    .querySelectorAll<HTMLElement>(`${MENU_SELECTOR}[${GENERATED_ATTR}]`)
    .forEach((section) => sectionObserver!.observe(section));
}

/**
 * Shows the sub-nav for the topmost menu section currently in the viewport and
 * hides the rest, so exactly one sub-nav is visible at a time. When no section
 * is intersecting (e.g. mid-scroll between two menus) the current sub-nav is
 * left in place, which also preserves the first sub-nav shown by default at
 * build time until scrolling begins.
 */
function showActiveSubNav(): void {
  // Generated sections sit in document order, so the first intersecting one is
  // the topmost menu in view.
  const sections = Array.from(
    document.querySelectorAll<HTMLElement>(`${MENU_SELECTOR}[${GENERATED_ATTR}]`)
  );
  const activeId =
    sections.find((section) => intersectingSections.has(section.id))?.id ?? null;
  if (!activeId) return;

  document
    .querySelectorAll<HTMLElement>(`${SUB_NAV_ITEMS_SELECTOR}[${GENERATED_ATTR}]`)
    .forEach((sub) => {
      sub.style.display =
        sub.getAttribute(NAV_TARGET_ATTR) === activeId ? "" : "none";
    });
}

/** The observer watching the generated menu-group sections, kept at module
 * scope so each render can disconnect the previous one before wiring up the new
 * nodes. */
let groupObserver: IntersectionObserver | null = null;

/**
 * Highlights the sub-nav item for whichever menu group is currently in the
 * viewport: looks up each generated group section from the id stamped on its
 * sub-nav item ({@link SUB_NAV_TARGET_ATTR}), observes it, and toggles
 * {@link ACTIVE_SUB_NAV_CLASS} on that item as its group enters and leaves view.
 * Items in hidden sub-navs are kept in sync too, so the highlight is already
 * correct when {@link showActiveSubNav} reveals their sub-nav.
 *
 * Disconnects any observer from a previous render so stale sections aren't
 * watched, and no-ops where `IntersectionObserver` isn't available.
 */
function observeActiveSubNav(): void {
  groupObserver?.disconnect();
  if (typeof IntersectionObserver === "undefined") return;

  groupObserver = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      document
        .querySelector<HTMLElement>(
          `[${SUB_NAV_TARGET_ATTR}="${entry.target.id}"]`
        )
        ?.classList.toggle(ACTIVE_SUB_NAV_CLASS, entry.isIntersecting);
    }
  }, sectionObserverOptions());

  document
    .querySelectorAll<HTMLElement>(`[${SUB_NAV_TARGET_ATTR}]`)
    .forEach((item) => {
      const targetId = item.getAttribute(SUB_NAV_TARGET_ATTR);
      const section = targetId ? document.getElementById(targetId) : null;
      if (section) groupObserver!.observe(section);
    });
}
